import { observer } from 'owa-mobx-react';
import loc from 'owa-localize';
import React from 'react';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { selectFocusedViewFilter } from 'owa-mail-triage-table-utils';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { getStore as getListViewStore, getViewFilterForTable } from 'owa-mail-list-store';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import type { IPivotItemProps } from '@fluentui/react/lib/Pivot';
import { getInboxPivotTheme } from 'owa-mail-densities/lib/utils/getInboxPivotTheme';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { allViewFilterText } from './PriorityInboxPivot.locstring.json';
import { lazySelectFilter } from 'owa-mail-filter-actions';
import { renderPriorityPivot } from './PriorityPivot';
import {
    full,
    medium,
    compact,
    nonSelectedPivotLink,
    selectedPivotLink,
    linkContent,
    pivotText,
    pivotRoot,
    pivotItem,
    pivotContainerParent,
    pivotItemContainer,
} from './InboxPivotStyles.scss';
import { logUsage } from 'owa-analytics';
import { setItem } from 'owa-local-storage';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';

import classnames from 'owa-classnames';

const LAST_SELECTED_PIVOT_KEY = 'mail_LAST_SELECTED_PIVOT';

export interface PriorityInboxPivotProps {
    tableViewId: string;
}

const defaultExport: React.FunctionComponent<PriorityInboxPivotProps> = observer(
    function PriorityInboxPivot(props: PriorityInboxPivotProps) {
        const tableView = getListViewStore().tableViews.get(props.tableViewId);
        const densityModeCssClass = getDensityModeCssClass(full, medium, compact);
        const pivotElementStyles = useComputedValue(
            () => ({
                link: classnames(nonSelectedPivotLink, densityModeCssClass),
                linkIsSelected: classnames(selectedPivotLink, densityModeCssClass),
                linkContent,
                text: classnames(pivotText, densityModeCssClass),
                root: classnames(pivotRoot, densityModeCssClass),
            }),
            [densityModeCssClass]
        );

        const onPivotItemClicked = React.useCallback(
            (
                item?: PivotItem | undefined,
                _ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
            ) => {
                if (item?.props.itemKey) {
                    if (item.props.itemKey === 'priority') {
                        setItem(
                            window,
                            `${LAST_SELECTED_PIVOT_KEY}${
                                getMailboxInfoFromTableViewId(props.tableViewId).userIdentity
                            }`,
                            'priority'
                        );

                        lazySelectFilter.importAndExecute(
                            'CopilotHighPriority',
                            'ListViewColumnHeader'
                        );
                    } else {
                        setItem(
                            window,
                            `${LAST_SELECTED_PIVOT_KEY}${
                                getMailboxInfoFromTableViewId(props.tableViewId).userIdentity
                            }`,
                            'notPriority'
                        );

                        logUsage(
                            'TnS_SelectAllPivot',
                            getMailboxInfoFromTableViewId(props.tableViewId)
                        );
                        selectFocusedViewFilter(-1, 'FocusedOtherPivot');
                    }
                }
            },
            [props.tableViewId]
        );

        const getKeyForSelectedFocusedViewFilter = (): string => {
            const currentSelectedFilter = getViewFilterForTable(tableView);
            if (currentSelectedFilter === 'CopilotHighPriority') {
                return 'priority';
            }
            return 'inbox';
        };

        const selectedKey = getKeyForSelectedFocusedViewFilter();

        const onRenderInboxLinkHandler = React.useCallback(
            (
                pivotProps?: IPivotItemProps,
                defaultRender?: (props?: IPivotItemProps) => JSX.Element | null
            ) => {
                return pivotProps && defaultRender ? (
                    <div
                        className={classnames(
                            pivotItem,
                            getDensityModeCssClass(full, medium, compact)
                        )}
                    >
                        <div className={classnames(pivotItemContainer, densityModeCssClass)}>
                            {defaultRender(pivotProps)}
                        </div>
                    </div>
                ) : null;
            },
            [densityModeCssClass]
        );

        const renderInboxPivot = (): JSX.Element => {
            return (
                <PivotItem
                    headerText={loc(allViewFilterText)}
                    itemKey={'inbox'}
                    onRenderItemLink={onRenderInboxLinkHandler}
                />
            );
        };

        const renderPriorityInboxPivot = React.useMemo(() => {
            return renderPriorityPivot();
        }, []);

        // Priority, All
        return (
            <ThemeProvider
                className={classnames(pivotContainerParent, densityModeCssClass)}
                applyTo="none"
                theme={getInboxPivotTheme(getDensityModeString())}
            >
                <Pivot
                    styles={pivotElementStyles}
                    selectedKey={`${selectedKey}`}
                    onLinkClick={onPivotItemClicked}
                >
                    {renderPriorityInboxPivot}
                    {renderInboxPivot()}
                </Pivot>
            </ThemeProvider>
        );
    },
    'PriorityInboxPivot'
);
export default defaultExport;
